// ─────────────────────────────────────────────────────────────────────────────
// NodesTable.js — the sortable list view of enriched nodes. Clicking a row opens
// the node detail modal (via onSelect); the PACKAGE and ⋯ cells stop that.
//
// COLUMNS is the single source of truth: it drives the header, the cells and the
// loading skeleton, so header order and cell order cannot drift apart. Each
// `cell` gets (node, handlers) — handlers carries the row actions, so a column
// can trigger something without the table plumbing a prop per column.
//
// Missing values render as "—" (see ../../format.js for the rule). NAME now
// carries a real value on every row: /enterprise-nodes serves it off the
// authority row, unlike the old /nodes surface whose enrich() dropped it.
// ─────────────────────────────────────────────────────────────────────────────
import styles from "./NodesTable.module.css";
import { StatusBadge, TrustScore, LoadValue, AlertBadge, statusColor } from "../badges";
import { DASH, dash, fmtCount, fmtLatency, fmtUptimeCell } from "../../format";
import RowMenu from "./rowActions/RowMenu";

const DownloadIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 3v11m0 0 4-4m-4 4-4-4" />
    <path d="M4 17v2a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-2" />
  </svg>
);

// The package button swallows the click so the row's detail modal doesn't open
// behind the one it triggers; RowMenu does the same for the ⋯ button.
const PackageCell = (node, { onDownload }) => (
  <button
    type="button"
    className={styles.download}
    onClick={(e) => { e.stopPropagation(); onDownload(node); }}
  >
    <DownloadIcon /> Download
  </button>
);

const COLUMNS = [
  { key: "node_id",         label: "NODE ID", cell: (n) => n.node_id || DASH,
    className: styles.tdId, style: (n) => ({ color: statusColor(n.status) }) },
  { key: "name",            label: "NAME",    cell: (n) => dash(n.name) },
  { key: "region",          label: "REGION",  cell: (n) => dash(n.region) },
  { key: "ip",              label: "IP",      mono: true, cell: (n) => dash(n.ip) },
  { key: "status",          label: "STATUS",  cell: (n) => <StatusBadge status={n.status} /> },
  { key: "latency_avg_ms",  label: "LATENCY", mono: true, cell: (n) => fmtLatency(n.latency_avg_ms) },
  { key: "uptime_seconds",  label: "UPTIME",  mono: true, cell: fmtUptimeCell },
  { key: "load_percent",    label: "LOAD",    cell: (n) => <LoadValue value={n.load_percent} /> },
  { key: "trust_score",     label: "TRUST",   cell: (n) => <TrustScore value={n.trust_score} /> },
  { key: "active_circuits", label: "USERS",   mono: true, cell: (n) => fmtCount(n.active_circuits) },
  { key: "alerts_count",    label: "ALERTS",  cell: (n) => <AlertBadge count={n.alerts_count} /> },
  { key: "package",         label: "PACKAGE", sortable: false, cell: PackageCell },
  { key: "actions",         label: "",        sortable: false,
    cell: (n, handlers) => <RowMenu node={n} actions={handlers} /> },
];

const cellClass = (col) => col.className ?? `${styles.td} ${col.mono ? styles.mono : ""}`;

export default function NodesTable({
  rows, loading, sortCol, sortDir, onSort, onSelect, selectedId,
  onDownload, onEdit, onDelete, isFiltered = false, canWrite = false,
}) {
  // Passed to every cell renderer. "View Details" is the same thing a row click
  // does, so it reuses onSelect rather than inventing a second path. `canWrite`
  // rides along so RowMenu can drop the entries a read-only viewer can't use.
  const handlers = { onDownload, onEdit, onDelete, onViewDetails: onSelect, canWrite };

  return (
    <div className={styles.wrap}>
      <table className={styles.table}>
        <thead>
          <tr>
            {COLUMNS.map((col) => {
              const sortable = col.sortable !== false;
              return (
                <th
                  key={col.key}
                  className={`${styles.th} ${sortable ? styles.thSortable : ""}`}
                  onClick={sortable ? () => onSort(col.key) : undefined}
                >
                  {col.label}
                  {sortable && sortCol === col.key && (
                    <span className={styles.sortArrow}>{sortDir === "asc" ? "↑" : "↓"}</span>
                  )}
                </th>
              );
            })}
          </tr>
        </thead>

        <tbody>
          {loading ? (
            Array.from({ length: 6 }).map((_, i) => (
              <tr key={i} className={styles.tr}>
                {COLUMNS.map((col) => (
                  <td key={col.key} className={styles.td}><span className={styles.skeleton} /></td>
                ))}
              </tr>
            ))
          ) : rows.length > 0 ? (
            rows.map((node, i) => (
              <tr
                key={node.node_id ?? i}
                className={`${styles.tr} ${selectedId === node.node_id ? styles.trActive : ""}`}
                onClick={() => onSelect(node)}
              >
                {COLUMNS.map((col) => (
                  <td key={col.key} className={cellClass(col)} style={col.style?.(node)}>
                    {col.cell(node, handlers)}
                  </td>
                ))}
              </tr>
            ))
          ) : (
            <tr>
              {/* An empty fleet and a filter that excluded everything are
                  different facts. Saying "no matches" to someone with no nodes
                  sends them hunting for a filter they never set. */}
              <td colSpan={COLUMNS.length} className={styles.empty}>
                {isFiltered ? "No nodes match your search." : "No nodes registered yet."}
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}
